'use client';

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { CheckCircle, AlertCircle, X } from 'lucide-react';

export type ToastType = 'success' | 'error';

export interface ToastItem {
  id: string;
  type: ToastType;
  msg: string;
  durationMs: number;
}

export interface ToastActions {
  success: (msg: string, durationMs?: number) => void;
  error: (msg: string, durationMs?: number) => void;
  show: (opts: { type: ToastType; msg: string; durationMs?: number }) => void;
}

const DEFAULT_DURATION_MS = 3500;

const ToastContext = createContext<ToastActions | null>(null);

export function useToastContext(): ToastActions {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counterRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(({ type, msg, durationMs = DEFAULT_DURATION_MS }: {
    type: ToastType;
    msg: string;
    durationMs?: number;
  }) => {
    const id = String(++counterRef.current);
    setToasts((prev) => [...prev, { id, type, msg, durationMs }]);
    setTimeout(() => dismiss(id), durationMs);
  }, [dismiss]);

  const success = useCallback((msg: string, durationMs?: number) => {
    show({ type: 'success', msg, durationMs });
  }, [show]);

  const error = useCallback((msg: string, durationMs?: number) => {
    show({ type: 'error', msg, durationMs });
  }, [show]);

  // Stable context value — without useMemo a new object identity each render
  // would re-trigger every consumer whose effects/callbacks depend on the
  // toast actions (e.g. a fetch in useCallback([...,toast]) → infinite loop).
  const actions = useMemo<ToastActions>(() => ({ success, error, show }), [success, error, show]);

  return (
    <ToastContext.Provider value={actions}>
      {children}
      <ToastRegion toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastRegion({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-label="Notifications"
      className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: string) => void }) {
  const isSuccess = toast.type === 'success';

  return (
    <div
      data-toast
      className={[
        'pointer-events-auto flex items-start gap-2.5 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium max-w-xs',
        isSuccess
          ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
          : 'bg-red-50 border-red-200 text-red-800',
      ].join(' ')}
    >
      {isSuccess
        ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
        : <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
      }
      <span className="flex-1">{toast.msg}</span>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className={[
          'shrink-0 p-0.5 rounded transition-colors',
          isSuccess ? 'text-emerald-400 hover:text-emerald-600' : 'text-red-400 hover:text-red-600',
        ].join(' ')}
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
